import { useState } from "react";
import { ArrowUpRight, Bot, Check, Heart, MapPin, MessageCircle, Plus, ShoppingBag } from "lucide-react";
import { LandingAppleMap } from "./LandingAppleMap";
import { ThetaMark } from "./ThetaMark";

type CardKind = "identity" | "person" | "agent" | "company" | "product";

const steps: { card: CardKind; label: string; title: string; description: string }[] = [
  {
    card: "identity",
    label: "Your card",
    title: "Your passkey. Your connections.",
    description: "Talk, buy, and connect from your own card.",
  },
  {
    card: "person",
    label: "People",
    title: "A conversation starts with a card.",
    description: "Meet someone, open their card, and start a conversation.",
  },
  {
    card: "company",
    label: "Company",
    title: "Your company gets its own place.",
    description: "Your story, your products, and the people who follow what you do.",
  },
  {
    card: "product",
    label: "Product",
    title: "From a photo to a storefront.",
    description: "A photo, the details, and a way to order.",
  },
  {
    card: "agent",
    label: "Bot",
    title: "A bot. A company. A connection.",
    description: "Connect a bot to the company behind the card.",
  },
];

export function LiquidCardShowcase() {
  const [active, setActive] = useState<CardKind>("identity");
  const [following, setFollowing] = useState(false);
  const [message, setMessage] = useState("");
  const step = steps.find((item) => item.card === active)!;

  const selectCard = (card: CardKind) => {
    setActive(card);
    setMessage("");
  };

  return (
    <section className="liquid-showcase" id="liquidcards" aria-labelledby="liquid-showcase-title">
      <h2 className="liquid-showcase-label" id="liquid-showcase-title">Your card</h2>

      <div className="liquid-showcase-steps" role="group" aria-label="Explore the cards">
        {steps.map((item) => (
          <button key={item.card} type="button" className="liquid-showcase-step" aria-pressed={active === item.card} onClick={() => selectCard(item.card)}>
            {item.label}
          </button>
        ))}
      </div>

      <div className="liquid-showcase-stage" data-active={active}>
        <LandingAppleMap />
        <article className="liquid-card liquid-card-identity" data-selected={active === "identity"} aria-label="Example passkey card">
          <img className="liquid-card-media" src="/landing-demo/identity-portrait.png" alt="Illustrative personal card portrait" />
          <div className="liquid-card-shade" aria-hidden="true" />
          <span className="liquid-card-kind">Example passkey</span>
          <div className="liquid-card-body">
            <span className="liquid-card-brand"><ThetaMark /></span>
            <h3>Your card</h3>
            <p>Your passkey. Your connections.</p>
            <div className="liquid-card-actions">
              <button type="button" className="liquid-card-action liquid-card-action-primary" disabled={active !== "identity"} onClick={() => { selectCard("person"); setMessage("Conversation preview — no message is sent."); }}>
                <MessageCircle size={15} aria-hidden="true" /> Talk
              </button>
              <button type="button" className="liquid-card-action" disabled={active !== "identity"} onClick={() => selectCard("product")}>
                <ShoppingBag size={15} aria-hidden="true" /> Buy
              </button>
              <button type="button" className="liquid-card-action" disabled={active !== "identity"} onClick={() => selectCard("agent")}>
                <Plus size={15} aria-hidden="true" /> Connect
              </button>
            </div>
          </div>
        </article>
        <article className="liquid-card liquid-card-person" data-selected={active === "person"} aria-label="Example person card">
          <img className="liquid-card-media" src="/landing-demo/person-portrait.png" alt="Illustrative person portrait" loading="lazy" />
          <div className="liquid-card-shade" aria-hidden="true" />
          <span className="liquid-card-kind">Example person</span>
          <div className="liquid-card-body">
            <span className="liquid-card-brand"><ThetaMark /></span>
            <h3>Say hello.</h3>
            <p>A person. A conversation. A new connection.</p>
            <div className="liquid-card-actions">
              <button type="button" className="liquid-card-action liquid-card-action-primary" disabled={active !== "person"} onClick={() => setMessage("Conversation preview — no message is sent.")}>
                <MessageCircle size={15} aria-hidden="true" /> Say hello
              </button>
            </div>
          </div>
        </article>
        <article className="liquid-card liquid-card-company" data-selected={active === "company"} aria-label="Olive Studio example company card">
          <img className="liquid-card-media" src="/landing-demo/studio-cover.png" alt="Illustrative Olive Studio cover" loading="lazy" />
          <div className="liquid-card-shade" aria-hidden="true" />
          <span className="liquid-card-kind">Example company</span>
          <div className="liquid-card-body">
            <span className="liquid-card-brand"><ThetaMark /></span>
            <h3>Olive Studio</h3>
            <p>A little studio.<br />Things made with care.</p>
            <span className="liquid-card-meta"><MapPin size={13} aria-hidden="true" /> A place in your world</span>
            <div className="liquid-card-actions">
              <button
                type="button"
                className="liquid-card-action"
                disabled={active !== "company"}
                aria-pressed={following}
                onClick={() => {
                  setFollowing((value) => !value);
                  setMessage("Demo follow only. Sign in to follow a real company.");
                }}
              >
                {following ? <Check size={15} aria-hidden="true" /> : <Heart size={15} aria-hidden="true" />}{following ? "Following" : "Follow"}
              </button>
              <button type="button" className="liquid-card-action liquid-card-action-primary" disabled={active !== "company"} onClick={() => selectCard("product")}>
                View product <ArrowUpRight size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        </article>

        <article className="liquid-card liquid-card-product" data-selected={active === "product"} aria-label="Everyday ceramics example product card">
          <img className="liquid-card-media" src="/landing-demo/ceramic-cups.png" alt="Illustrative ceramic cups" loading="lazy" />
          <div className="liquid-card-shade" aria-hidden="true" />
          <span className="liquid-card-kind">Example product</span>
          <div className="liquid-card-body">
            <span className="liquid-card-brand"><ShoppingBag size={14} aria-hidden="true" /> From Olive Studio</span>
            <h3>Everyday<br />ceramics</h3>
            <p>For slow mornings<br />and one more cup.</p>
            <span className="liquid-card-meta">A sample product. A new possibility.</span>
            <div className="liquid-card-actions">
              <button type="button" className="liquid-card-action liquid-card-action-primary" disabled={active !== "product"} onClick={() => setMessage("Demo order preview — no purchase is placed.")}>
                Order <ShoppingBag size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        </article>

        <article className="liquid-card liquid-card-agent" data-selected={active === "agent"} aria-label="Studio assistant example agent card">
          <div className="liquid-card-bot-art" aria-hidden="true"><span /><span /><Bot size={94} strokeWidth={1.15} /><span className="liquid-card-bot-spark"><Plus size={19} /></span></div>
          <span className="liquid-card-kind">Example agent</span>
          <div className="liquid-card-body">
            <span className="liquid-card-brand"><ThetaMark /></span>
            <h3>Studio<br />assistant</h3>
            <p>A bot connected to<br />the company behind the card.</p>
            <div className="liquid-card-tags"><span>Company context</span><span>Product discovery</span></div>
            <div className="liquid-card-actions">
              <button type="button" className="liquid-card-action liquid-card-action-primary" disabled={active !== "agent"} onClick={() => setMessage("Demo: your bot connects to this company. Sign in to configure a real bot.")}>
                Connect bot <ArrowUpRight size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        </article>
      </div>

      <div className="liquid-showcase-detail" aria-live="polite" aria-atomic="true">
        <h3>{step.title}</h3>
        <p>{message || step.description}</p>
      </div>
      <p className="liquid-showcase-disclaimer">Example cards · preview actions only</p>
    </section>
  );
}
